import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { createMessageAPI } from "@/api/apiFactory";
import type { Conversation, Message } from "@/types/Message";

// Helper function to play notification sound
const playNotificationSound = () => {
  try {
    // Create a simple notification sound using Web Audio API
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800; // Frequency in Hz
    oscillator.type = "sine";

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (error) {
    console.warn("Could not play notification sound:", error);
  }
};

// Helper function to show browser notification
const showBrowserNotification = async (message: Message) => {
  try {
    // Request permission if not already granted
    if (Notification.permission === "default") {
      await Notification.requestPermission();
    }

    // Show notification if permission is granted
    if (Notification.permission === "granted") {
      const notification = new Notification("New Message", {
        body: message.content,
        icon: "/favicon.ico", // Use your app icon
        badge: "/favicon.ico",
        tag: message.conversationId, // Prevents duplicate notifications for the same conversation
        requireInteraction: false,
      });

      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);
    }
  } catch (error) {
    console.warn("Could not show browser notification:", error);
  }
};

export function useMessages(memberId: string) {
  const apiRef = useRef(createMessageAPI());
  const api = apiRef.current;
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentConversationIdRef = useRef<string | null>(null);
  const listenersRegisteredRef = useRef(false);

  // Single source of truth for the unread badge: the sum of this member's per-conversation
  // unread counts. Deriving it (rather than tracking a separate counter that drifts) means
  // the badge always agrees with the blue dots — clearing a conversation's count updates both.
  const unreadCount = useMemo(
    () => conversations.reduce((sum, conv) => sum + (conv.unreadCount?.[memberId] || 0), 0),
    [conversations, memberId],
  );

  const loadConversations = useCallback(async () => {
    try {
      const convs = await api.getConversations(memberId);
      setConversations(convs || []);
      // Clear any previous error on successful load
      setError(null);
    } catch (err) {
      console.error("Error loading conversations:", err);
      setError(err instanceof Error ? err.message : "Failed to load conversations");
    }
  }, [api, memberId]);

  useEffect(() => {
    if (!memberId) return;

    const initializeMessaging = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Connect to the WebSocket — this will resolve even if the socket
        // hasn't connected yet (after a timeout). REST calls work independently.
        await api.connect();

        // Load conversations via REST — independent of socket state
        await loadConversations();

        // Only register event listeners once to avoid duplicates
        if (!listenersRegisteredRef.current) {
          listenersRegisteredRef.current = true;

          // Listen for new messages
          api.onNewMessage((message) => {
            console.log("New message received:", message);

            // If this is a message for us (we're the recipient), alert the member. The badge
            // itself updates via the loadConversations() refetch below (derived count).
            if (message.recipientId === memberId) {
              // Play notification sound
              playNotificationSound();

              // Show browser notification
              showBrowserNotification(message);
            }

            // Refresh conversation list for both sender and recipient
            loadConversations();

            // If the message belongs to the currently open conversation, update messages immediately
            if (currentConversationIdRef.current === message.conversationId) {
              setMessages((prevMessages) => [...prevMessages, message]);
            }
          });

          // Listen for messages being marked as read
          api.onMessageRead(() => {
            console.log("Message marked as read - refreshing conversations");
            loadConversations();
          });

          // Listen for a whole conversation being read (by us — possibly from another tab or
          // the sidebar instance). Zero its unread count locally so the blue dot clears and the
          // badge drops by exactly that conversation's unread count. No refetch, no race.
          api.onConversationRead((data) => {
            if (data.memberId !== memberId) return;
            setConversations((prev) =>
              prev.map((conv) =>
                conv.id === data.conversationId
                  ? { ...conv, unreadCount: { ...conv.unreadCount, [memberId]: 0 } }
                  : conv,
              ),
            );
          });

          // When the socket reconnects (e.g. after network drop), refresh data
          api.onReconnect(() => {
            console.log("WebSocket reconnected — refreshing conversations");
            loadConversations();
          });
        }

        setIsLoading(false);
      } catch (err) {
        console.error("Error initializing messaging:", err);
        setError(err instanceof Error ? err.message : "Failed to load messages");
        setIsLoading(false);
      }
    };

    initializeMessaging();

    // Cleanup
    return () => {
      api.disconnect();
      listenersRegisteredRef.current = false;
    };
  }, [memberId, api, loadConversations]);

  const loadConversationMessages = async (conversationId: string) => {
    try {
      setIsLoading(true);
      currentConversationIdRef.current = conversationId;
      const msgs = await api.getConversationMessages(conversationId);
      setMessages(msgs || []);
      setIsLoading(false);
    } catch (err) {
      console.error("Error loading conversation messages:", err);
      setError(err instanceof Error ? err.message : "Failed to load messages");
      setIsLoading(false);
    }
  };

  const sendMessage = async (params: {
    type: "member" | "group" | "conversation";
    recipientId?: string;
    locationIds?: string[];
    conversationId?: string;
    content: string;
  }) => {
    try {
      if (params.type === "conversation" && params.conversationId) {
        // Send to existing conversation (used for custom groups)
        await api.sendMessageToConversation(params.conversationId, params.content);
      } else if (params.type === "group" && params.locationIds) {
        // Send to location-based group
        await api.sendGroupMessage(params.locationIds, params.content);
      } else if (params.type === "member" && params.recipientId) {
        // Send to individual
        await api.sendMessage(params.recipientId, params.content);
      } else {
        throw new Error("Invalid message parameters");
      }
      await loadConversations(); // Refresh conversation list
    } catch (err) {
      // Don't set error state here - let the caller handle it with a toast
      // Error state is reserved for initialization/loading failures
      throw err;
    }
  };

  const markAsRead = async (messageId: string) => {
    try {
      await api.markAsRead(messageId, memberId);
      // Don't call loadConversations here - let the broadcast handle it
      // This avoids race conditions where we load stale data
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to mark message as read");
    }
  };

  // Mark a whole conversation as read in one go (what "opening" a conversation should do).
  // Optimistically zeroes this member's unread count for the conversation so the blue dot and
  // badge update instantly, then persists server-side and broadcasts conversation_read.
  const markConversationRead = useCallback(
    async (conversationId: string) => {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId
            ? { ...conv, unreadCount: { ...conv.unreadCount, [memberId]: 0 } }
            : conv,
        ),
      );
      try {
        await api.markConversationRead(conversationId);
      } catch (err) {
        console.error("Failed to mark conversation as read:", err);
      }
    },
    [api, memberId],
  );

  return {
    conversations,
    messages,
    unreadCount,
    isLoading,
    error,
    sendMessage,
    loadConversationMessages,
    loadConversations,
    markAsRead,
    markConversationRead,
  };
}
